/* eslint-disable max-lines-per-function, max-len, max-lines, react/prop-types */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import EditActionIcon from '../../../Assets/images/svg/dar_pencil.svg';
import DeleteActionIcon from '../../../Assets/images/svg/dar_delete.svg';
import './CsatCustomerDetails.scss';
import UseAxios from '../../../Api/http/http';
import {
  createCsatCustomerDetails,
  updateCsatCustomerDetails,
  deleteCsatCustomerDetails,
  getProjectDetailsList,
} from '../../../Api/request/projectDetails';
import { useAuth } from '../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../Constants/modules';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import { buildCsatCustomerDetailsTableConfig } from './CsatCustomerDetailsConfig';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

/* -------------------------------------------------------------------------- */
/*  Base URL                                                                   */
/* -------------------------------------------------------------------------- */

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api/v1/csat-customer-details`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api/v1/csat-customer-details`;
};
const baseURL = fetchBaseURL();

/* -------------------------------------------------------------------------- */
/*  Row normalisation                                                          */
/* -------------------------------------------------------------------------- */

const normalizeRow = (row) => ({
  ...row,
  customerId: row.customerId ?? row.customer_id ?? null,
  projectId: row.projectId ?? row.project_id ?? '',
  projectSeqId: row.projectSeqId ?? row.project_seq_id ?? '',
  projectName: row.projectName ?? row.project_name ?? '',
  customerName: row.customerName ?? row.customer_name ?? '',
  customerEmail: row.customerEmail ?? row.customer_email ?? '',
  comments: row.comments ?? '',
});

/* -------------------------------------------------------------------------- */
/*  Project-list helpers (for filter options)                                  */
/* -------------------------------------------------------------------------- */

const getProjectRows = (response = {}) => {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.tableData)) return payload.tableData;
  if (Array.isArray(payload)) return payload;
  return [];
};

const normalizeProjectRow = (row) => ({
  projectId: String(row?.projectId ?? row?.project_id ?? row?.id ?? row?.project_seq_id ?? '').trim(),
  projectSeqId: String(row?.projectSeqId ?? row?.project_seq_id ?? '').trim(),
  projectName: String(row?.projectName ?? row?.project_name ?? row?.name ?? '').trim(),
});

const buildUniqueFilterOptions = (rows, selector) => Array.from(
  new Set(rows.map((row) => String(selector(row) ?? '').trim()).filter(Boolean)),
).sort((left, right) => String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' }));

// Forces the given field(s) to show as selected in their column filter menu items.
// targetByField: { [columnField]: targetValueString }
const forceColumnFilterSelection = (config, targetByField) => {
  if (!Array.isArray(config) || !targetByField) return config;
  return config.map((column) => {
    const targetValue = targetByField[column.field];
    if (targetValue === undefined) return column;
    return {
      ...column,
      menuItems: (column.menuItems || []).map((menu) => ({
        ...menu,
        items: (menu.items || []).map((item) => {
          if (item.type === 'filter') {
            const options = item.options || item.values || [];
            const matchedOption = options.find(
              (opt) => String(opt.value ?? opt.id ?? opt).trim() === targetValue,
            ) || {
              id: targetValue, name: targetValue, label: targetValue, value: targetValue,
            };

            return {
              ...item,
              selectedValue: matchedOption,
              selectedValues: [matchedOption],
            };
          }
          return item;
        }),
      })),
    };
  });
};

/* -------------------------------------------------------------------------- */
/*  Module-level API response transformer  (mirrors ProjectEmployeeCost)      */
/*                                                                             */
/*  This is a PLAIN function (no React deps) that maps the API's              */
/*  csatCustomerDetailsList key into the tableData/tableConfiguration shape.  */
/* -------------------------------------------------------------------------- */

const handleCsatApiResponse = (response, tableConfigurationCacheRef) => {
  if (!response) return;

  const rawData = response?.data ?? response;

  // Extract the list regardless of which key the API uses
  const rawList
    = rawData?.csatCustomerDetailsList
    ?? rawData?.data?.csatCustomerDetailsList
    ?? rawData?.tableData
    ?? rawData?.data?.tableData
    ?? rawData?.data
    ?? [];

  const rows = (Array.isArray(rawList) ? rawList : []).map(normalizeRow);

  // Re-use last known tableConfiguration when the result set is empty (keeps
  // toolbar filter icon visible) – identical to ProjectEmployeeCost behaviour.
  let tableConfiguration = [];
  if (Array.isArray(response.data?.tableConfiguration) && response.data.tableConfiguration.length) {
    tableConfiguration = response.data.tableConfiguration;
    if (tableConfigurationCacheRef) tableConfigurationCacheRef.current = tableConfiguration;
  } else if (tableConfigurationCacheRef?.current?.length) {
    tableConfiguration = tableConfigurationCacheRef.current;
  }

  // Pagination
  const rawPagination
    = rawData?.pagination
    ?? rawData?.paginationConfig
    ?? rawData?.data?.pagination
    ?? rawData?.data?.paginationConfig
    ?? response?.meta
    ?? null;
  const total
    = rawPagination?.total
    ?? rawPagination?.count
    ?? response?.meta?.total
    ?? rows.length;
  const page = rawPagination?.page ?? response?.meta?.page ?? 1;
  const limit = rawPagination?.limit ?? response?.meta?.limit ?? 10;

  // Mutate response in-place — same pattern as ProjectEmployeeCost
  response.data = {
    ...rawData,
    tableConfiguration,
    tableData: rows,
    data: {
      ...(rawData?.data || {}),
      tableConfiguration,
      tableData: rows,
    },
  };

  response.meta = {
    ...(response.meta || {}),
    page,
    limit,
    total,
  };
};

/* -------------------------------------------------------------------------- */
/*  More Actions (three-dot) toolbar menu — PrimeReact Menu (popup, body)    */
/* -------------------------------------------------------------------------- */

const CsatMoreActionsMenu = () => {
  const navigate = useNavigate();
  const moreMenuRef = useRef(null);

  const menuItems = useMemo(() => [
    {
      label: 'CSAT History',
      command: () => navigate('/projects/csat-history'),
    },
  ], [navigate]);

  return (
    <React.Fragment>
      <Menu
        ref={moreMenuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        className="csat-cd-more-menu-popup"
      />
      <Button
        type="button"
        icon="pi pi-ellipsis-v"
        className="p-button-text csat-cd-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        aria-label="More Actions"
        tooltip="More options"
        tooltipOptions={{ position: 'top' }}
      />
    </React.Fragment>
  );
};

/* -------------------------------------------------------------------------- */
/*  Customer Form Dialog (Add / Edit)                                          */
/* -------------------------------------------------------------------------- */

const CustomerFormDialog = ({
  visible,
  mode,
  rowData,
  onHide,
  onSuccess,
  token,
  projectOptions = [],
  defaultProjectId = '',
}) => {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({ projectId: '', customerName: '', customerEmail: '', comments: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!visible) return;
    setError('');
    if (isEdit && rowData) {
      setForm({
        projectId: String(rowData.projectId ?? rowData.project_id ?? defaultProjectId ?? ''),
        customerName: rowData.customerName ?? rowData.customer_name ?? '',
        customerEmail: rowData.customerEmail ?? rowData.customer_email ?? '',
        comments: rowData.comments ?? '',
      });
    } else {
      setForm({
        projectId: String(defaultProjectId ?? '').trim(),
        customerName: '',
        customerEmail: '',
        comments: '',
      });
    }
    // Only re-seed the form when the dialog opens (or switches row) — not on
    // every later `defaultProjectId` change, since the parent strips the
    // `?projectId=` query param shortly after the table applies its filter,
    // which would otherwise reset an already-selected Project Name back to blank.
  }, [visible, isEdit, rowData]);

  const selectedProjectName = useMemo(() => {
    const match = (Array.isArray(projectOptions) ? projectOptions : []).find(
      (option) => String(option.projectId) === String(form.projectId),
    );
    return match?.projectName || '';
  }, [form.projectId, projectOptions]);

  if (!visible) return null;

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.projectId.trim()) {
      setError('Project Name is required.');
      return;
    }
    if (!form.customerName.trim()) {
      setError('Customer Name is required.');
      return;
    }
    if (!form.customerEmail.trim()) {
      setError('Customer Email is required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.customerEmail.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        projectId: isEdit ? (rowData.projectId ?? rowData.project_id ?? form.projectId) : Number(form.projectId.trim()),
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        comments: form.comments.trim(),
      };
      const req = isEdit
        ? updateCsatCustomerDetails(rowData.customerId ?? rowData.customer_id, payload)
        : createCsatCustomerDetails(payload);
      const result = await UseAxios({ ...req, headers: { ...req.headers, token } });
      if (result && (result.status === 200 || result.status === 201)) {
        onSuccess?.(isEdit ? 'Customer updated successfully.' : 'Customer added successfully.', form.projectId);
        onHide();
      } else {
        setError(result?.data?.message || 'Operation failed. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Operation failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ms-dialog-overlay csat-cd-dialog-overlay" role="dialog" aria-modal="true">
      <div className="ms-dialog ms-dialog--md ms-dialog--csat-cd-form">
        <div className="ms-dialog-header">
          <span className="ms-dialog-title">
            {isEdit ? 'Edit Customer Details' : 'Add Customer Details'}
          </span>
        </div>
        <div className="ms-dialog-form ms-dialog-form--csat-cd">
          <div className="ms-dialog-field">
            <label className="ms-dialog-label">
              Project Name <span className="ms-dialog-required">*</span>
            </label>
            {isEdit ? (
              <input
                type="text"
                className="ms-dialog-input ms-dialog-input--readonly"
                value={selectedProjectName || form.projectId || ''}
                readOnly
                disabled={submitting}
              />
            ) : (
              <select
                className="ms-dialog-input ms-dialog-select"
                value={form.projectId}
                onChange={(ev) => setField('projectId', ev.target.value)}
                disabled={submitting}
              >
                <option value="">Select</option>
                {projectOptions.map((option) => (
                  <option key={String(option.projectId)} value={String(option.projectId)}>
                    {option.projectName}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="ms-dialog-field">
            <label className="ms-dialog-label">Customer Name <span className="ms-dialog-required">*</span></label>
            <input type="text" className="ms-dialog-input" placeholder="Enter Customer Name" value={form.customerName} onChange={(ev) => setField('customerName', ev.target.value)} disabled={submitting} />
          </div>
          <div className="ms-dialog-field">
            <label className="ms-dialog-label">Customer Email <span className="ms-dialog-required">*</span></label>
            <input type="email" className="ms-dialog-input" placeholder="Enter Customer Email" value={form.customerEmail} onChange={(ev) => setField('customerEmail', ev.target.value)} disabled={submitting} />
          </div>
          <div className="ms-dialog-field ms-dialog-field--fullrow">
            <label className="ms-dialog-label">Comments</label>
            <textarea className="ms-dialog-textarea" placeholder="Enter Comment..." value={form.comments} onChange={(ev) => setField('comments', ev.target.value)} disabled={submitting} rows={3} />
          </div>
        </div>
        {error && <div className="ms-dialog-error">{error}</div>}
        <div className="ms-dialog-footer">
          <button type="button" className="ms-btn ms-btn--ghost" onClick={onHide} disabled={submitting}>Cancel</button>
          <button type="button" className="ms-btn ms-btn--primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Delete Confirmation Modal                                                  */
/* -------------------------------------------------------------------------- */

const DeleteModal = ({ isOpen, onClose, onConfirm, isSubmitting = false }) => {
  if (!isOpen) return null;
  return (
    <div className="csat-delete-backdrop" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
      <div className="csat-delete-modal">
        <div className="csat-delete-modal__header">Delete Customer Details</div>
        <div className="csat-delete-modal__body">
          <div className="csat-delete-modal__warning" aria-hidden="true" />
          <div className="csat-delete-modal__message">Are you sure you want to delete this customer record?</div>
        </div>
        <div className="csat-delete-modal__footer">
          <button type="button" className="csat-delete-modal__btn csat-delete-modal__btn--ghost" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button type="button" className="csat-delete-modal__btn csat-delete-modal__btn--primary" onClick={onConfirm} disabled={isSubmitting}>Delete</button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main Page                                                                  */
/* -------------------------------------------------------------------------- */

const CsatCustomerDetails = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdParam = searchParams.get('projectId');
  const initialProjectIdRef = useRef(projectIdParam);
  const isInitialFilterApplied = useRef(false);
  const [selectedProjectIdOverride, setSelectedProjectIdOverride] = useState(null);
  const customerFilterOptionsRef = useRef({ customerName: [], customerEmail: [] });
  const permissions = useMemo(() => userProfile?.permissions || {}, [userProfile]);

  const hasPermission = useCallback(
    (moduleName, action) => {
      const actions = Array.isArray(permissions?.[moduleName]) ? permissions[moduleName] : [];
      return actions.includes(action);
    },
    [permissions],
  );

  const canView = useMemo(
    () =>
      hasPermission(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'read')
      || hasPermission(MODULE_NAMES.DELIVERY_PROJECT_OVERVIEW_CSAT, 'read')
      || hasPermission(MODULE_NAMES.MIS_CSAT_HISTORY, 'read'),
    [hasPermission],
  );
  const canCreate = useMemo(() => hasPermission(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'create'), [hasPermission]);
  const canUpdate = useMemo(() => hasPermission(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'update'), [hasPermission]);
  const canDelete = useMemo(() => hasPermission(MODULE_NAMES.MIS_CSAT_CUSTOMER_DETAILS, 'delete'), [hasPermission]);

  const [tableRefreshNonce, setTableRefreshNonce] = useState(0);
  const refreshTable = useCallback(() => setTableRefreshNonce((nonce) => nonce + 1), []);

  const currentPageRef = useRef(1);
  const tableStateRef = useRef(null);
  // Cache last known tableConfiguration so toolbar filter stays visible on empty results
  const tableConfigurationCacheRef = useRef([]);
  const lastFiltersJsonRef = useRef(null);

  // Pagination state
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const handlePaginationChange = useCallback((newPagination) => {
    currentPageRef.current = Number(newPagination.page) || 1;
    setPagination(newPagination);
  }, []);

  // Dialog state
  const [dialog, setDialog] = useState({ type: null, row: null });
  const openAdd = useCallback(() => setDialog({ type: 'add', row: null }), []);
  const openEdit = useCallback((row) => setDialog({ type: 'edit', row }), []);
  const openDelete = useCallback((row) => setDialog({ type: 'delete', row }), []);
  const closeDialog = useCallback(() => setDialog({ type: null, row: null }), []);

  // Auto-open add dialog when navigated from Project Overview "Add customer details" link
  useEffect(() => {
    if (location.state?.openAdd && canCreate) {
      openAdd();
      // Clear the state so a page refresh does not re-trigger the dialog
      window.history.replaceState({}, '');
    }
  }, [location.state?.openAdd, canCreate, openAdd]);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  // Project filter options loaded once from project_details.
  // projectFilterOptionsRef is kept in sync with the state so that
  // handleFilteredApiResponse can read the latest value without having
  // projectFilterOptions in its useCallback deps (which would cause the
  // callback — and the configuration — to rebuild on every data fetch).
  const projectFilterOptionsRef = useRef({ projectId: [], projectName: [] });
  const [projectFilterOptions, setProjectFilterOptions] = useState({ projectId: [], projectName: [] });
  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    let mounted = true;
    UseAxios(getProjectDetailsList()).then((response) => {
      if (!mounted) return;
      const rows = getProjectRows(response)
        .map(normalizeProjectRow)
        .filter((row) => row.projectId && row.projectName);
      const uniqueRows = Array.from(
        new Map(rows.map((row) => [row.projectId, row])).values(),
      ).sort((left, right) => left.projectName.localeCompare(right.projectName, undefined, { numeric: true, sensitivity: 'base' }));
      const newFilterOpts = {
        projectId: buildUniqueFilterOptions(uniqueRows, (row) => row.projectId),
        projectName: buildUniqueFilterOptions(uniqueRows, (row) => row.projectName),
      };
      projectFilterOptionsRef.current = newFilterOpts;
      setProjectFilterOptions(newFilterOpts);
      setProjectOptions(uniqueRows);
    })
      .catch(() => { /* non-critical */ });
    return () => {
      mounted = false;
    };
  }, []);

  const accessibleProjectOptions = useMemo(() => {
    const allowedProjectIds = new Set(
      (projectFilterOptions.projectId || [])
        .map((item) => String(item?.value ?? item?.id ?? item?.name ?? item ?? '').trim())
        .filter(Boolean),
    );
    const allowedProjectNames = new Set(
      (projectFilterOptions.projectName || [])
        .map((item) => String(item?.name ?? item?.label ?? item?.value ?? item ?? '').trim())
        .filter(Boolean),
    );

    if (allowedProjectIds.size === 0 && allowedProjectNames.size === 0) {
      return projectOptions;
    }

    const targetProjId = String(selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current || '').trim();

    return projectOptions.filter((option) => {
      const projId = String(option.projectId ?? '').trim();
      const projSeqId = String(option.projectSeqId ?? '').trim();
      const projName = String(option.projectName ?? '').trim();
      if (targetProjId && (projId === targetProjId || projSeqId === targetProjId)) return true;
      return allowedProjectIds.has(projId) || allowedProjectIds.has(projSeqId) || allowedProjectNames.has(projName);
    });
  }, [projectOptions, projectFilterOptions, selectedProjectIdOverride, projectIdParam]);

  useEffect(() => {
    const targetProjId = selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current;
    if (!targetProjId || isInitialFilterApplied.current) return;

    const timer = setTimeout(() => {
      const tableApi = tableStateRef.current;
      if (!tableApi) return;

      const trimmedId = String(targetProjId).trim();
      const matchedProject = projectOptions.find(
        (opt) => String(opt.projectId).trim() === trimmedId || String(opt.projectSeqId).trim() === trimmedId,
      );

      const projIdOption = { id: trimmedId, name: trimmedId, label: trimmedId, value: trimmedId };
      const existingFilters = tableApi.filters || [];
      const updatedFilters = [
        ...existingFilters.filter((filterItem) => filterItem.field !== 'projectId' && filterItem.field !== 'projectName'),
        { field: 'projectId', selectedValue: projIdOption, selectedValues: [projIdOption] },
      ];

      if (matchedProject?.projectName && matchedProject.projectName !== trimmedId) {
        const trimmedName = matchedProject.projectName;
        const projNameOption = {
          id: trimmedName, name: trimmedName, label: trimmedName, value: trimmedName,
        };
        updatedFilters.push({ field: 'projectName', selectedValue: projNameOption, selectedValues: [projNameOption] });
      }

      tableApi.setFilters?.(updatedFilters);
      tableApi.fetchTableData?.(
        updatedFilters,
        { limit: pagination.limit || 10, page: 1 },
        '',
      );

      isInitialFilterApplied.current = true;
    }, 500);

    return () => clearTimeout(timer);
  }, [projectIdParam, selectedProjectIdOverride, pagination.limit, projectOptions]);

  const [tableConfig, setTableConfig] = useState(() => buildCsatCustomerDetailsTableConfig({}));

  useEffect(() => {
    if (projectFilterOptions.projectName?.length > 0 || projectFilterOptions.projectId?.length > 0) {
      const freshFilterOptions = {
        projectId: projectFilterOptions.projectId,
        projectName: projectFilterOptions.projectName,
        customerName: customerFilterOptionsRef.current.customerName,
        customerEmail: customerFilterOptionsRef.current.customerEmail,
      };
      let freshConfig = buildCsatCustomerDetailsTableConfig(freshFilterOptions);

      const initialProjId = selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current;
      if (initialProjId && freshConfig.length > 0) {
        const targetProjId = String(initialProjId).trim();
        const matchedProjectRow = projectOptions.find(
          (opt) => String(opt.projectId).trim() === targetProjId || String(opt.projectSeqId).trim() === targetProjId,
        );
        const targetSelection = { projectId: targetProjId };
        if (matchedProjectRow?.projectName && matchedProjectRow.projectName !== targetProjId) {
          targetSelection.projectName = matchedProjectRow.projectName;
        }
        freshConfig = forceColumnFilterSelection(freshConfig, targetSelection);
      }

      setTableConfig(freshConfig);
    }
  }, [projectFilterOptions, projectOptions, projectIdParam, selectedProjectIdOverride]);

  /* ── handleFilteredApiRequest ────────────────────────────────────────── */
  const handleFilteredApiRequest = (requestData) => {
    let activeFilters = requestData.filter || [];

    const targetProjId = selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current;
    if (targetProjId) {
      const existingProjIdFilter = activeFilters.find((item) => item?.field === 'projectId');
      const existingProjNameFilter = activeFilters.find((item) => item?.field === 'projectName');

      const hasExplicitProjId = existingProjIdFilter && (
        (existingProjIdFilter.selectedValue != null && existingProjIdFilter.selectedValue !== '')
        || (Array.isArray(existingProjIdFilter.selectedValues) && existingProjIdFilter.selectedValues.length > 0)
      );
      const hasExplicitProjName = existingProjNameFilter && (
        (existingProjNameFilter.selectedValue != null && existingProjNameFilter.selectedValue !== '')
        || (Array.isArray(existingProjNameFilter.selectedValues) && existingProjNameFilter.selectedValues.length > 0)
      );

      const userClearedFilter = isInitialFilterApplied.current && (
        (existingProjIdFilter && Array.isArray(existingProjIdFilter.selectedValues) && existingProjIdFilter.selectedValues.length === 0)
        || (existingProjNameFilter && Array.isArray(existingProjNameFilter.selectedValues) && existingProjNameFilter.selectedValues.length === 0)
      );

      if (!hasExplicitProjId && !hasExplicitProjName && !userClearedFilter) {
        const trimmedId = String(targetProjId).trim();
        const matchedProjectRow = projectOptions.find(
          (opt) => String(opt.projectId).trim() === trimmedId || String(opt.projectSeqId).trim() === trimmedId,
        );
        const projIdOption = { id: trimmedId, name: trimmedId, label: trimmedId, value: trimmedId };
        const newFilters = [
          ...activeFilters.filter((filterItem) => filterItem.field !== 'projectId' && filterItem.field !== 'projectName'),
          {
            field: 'projectId',
            selectedValue: projIdOption,
            selectedValues: [projIdOption],
          },
        ];

        const matchedName = matchedProjectRow?.projectName;
        if (matchedName && matchedName !== trimmedId) {
          const projNameOption = { id: matchedName, name: matchedName, label: matchedName, value: matchedName };
          newFilters.push({
            field: 'projectName',
            selectedValue: projNameOption,
            selectedValues: [projNameOption],
          });
        }

        activeFilters = newFilters;
        requestData.filter = activeFilters;
      }
    }

    isInitialFilterApplied.current = true;

    const currentFiltersJson = JSON.stringify(activeFilters);
    const filterChanged = lastFiltersJsonRef.current !== null
      && lastFiltersJsonRef.current !== currentFiltersJson;
    lastFiltersJsonRef.current = currentFiltersJson;

    let page = Number(requestData?.pagination?.page) || 1;
    const limit = Number(requestData?.pagination?.limit) || 10;

    if (filterChanged) {
      page = 1;
    }
    currentPageRef.current = page;

    // Mutate requestData in place AND return the enriched copy
    requestData.page = page;
    requestData.limit = limit;
    if (requestData.pagination) {
      requestData.pagination.page = page;
      requestData.pagination.limit = limit;
    }

    return {
      ...requestData,
      page,
      limit,
      filter: activeFilters,
      pagination: { page, limit },
      payload: {
        userInfo: {
          id: userProfile?.id,
          emailId: userProfile?.email,
          employeeId: userProfile?.employeeId,
          name: userProfile?.name,
          permissions: userProfile?.permissions,
          roleDetails: userProfile?.roleDetails,
        },
      },
    };
  };

  /* ── handleFilteredApiResponse  (mirrors ProjectEmployeeCost pattern) ──── */
  //
  // Calls the module-level handleCsatApiResponse (pure transform, no React
  // deps) first — identical to how ProjectEmployeeCost calls its module-level
  // handleFilteredApiResponse — then enriches rows with action buttons and
  // updates React state.
  const handleFilteredApiResponse = useCallback(
    (response) => {
      if (!response) return;

      // 1. Pure data transform — mutates response.data in-place
      handleCsatApiResponse(response, tableConfigurationCacheRef);

      // 2. Extract the normalised rows that handleCsatApiResponse put in
      const rows = Array.isArray(response.data?.tableData) ? response.data.tableData : [];

      // 3. Build filter options from loaded rows
      const filterOpts = response?.filterOptions
        || response?.data?.filterOptions
        || response?.data?.data?.filterOptions
        || {};

      // Prefer the full list returned by the API (all records in the system).
      // Fall back to building from visible rows for backward compatibility.
      const customerNameOpts = Array.isArray(filterOpts.customerName) && filterOpts.customerName.length > 0
        ? filterOpts.customerName
        : buildUniqueFilterOptions(rows, (row) => row.customerName);
      const customerEmailOpts = Array.isArray(filterOpts.customerEmail) && filterOpts.customerEmail.length > 0
        ? filterOpts.customerEmail
        : buildUniqueFilterOptions(rows, (row) => row.customerEmail);

      customerFilterOptionsRef.current = {
        customerName: customerNameOpts,
        customerEmail: customerEmailOpts,
      };

      if (filterOpts.projectId || filterOpts.projectName) {
        const updatedFilterOpts = {
          projectId: filterOpts.projectId || [],
          projectName: filterOpts.projectName || [],
        };
        projectFilterOptionsRef.current = updatedFilterOpts;
        setProjectFilterOptions(updatedFilterOpts);
      }

      const freshFilterOptions = {
        projectId: filterOpts.projectId || projectFilterOptionsRef.current.projectId || [],
        projectName: filterOpts.projectName || projectFilterOptionsRef.current.projectName || [],
        customerName: customerNameOpts,
        customerEmail: customerEmailOpts,
      };
      let freshConfig = buildCsatCustomerDetailsTableConfig(freshFilterOptions);

      // If a project was explicitly selected (Add dialog) or is present in the URL,
      // force the UI to show it as selected in both the projectId and projectName filters.
      const initialProjId = selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current;
      if (initialProjId && freshConfig.length > 0) {
        const targetProjId = String(initialProjId).trim();
        const matchedProjectRow = projectOptions.find((opt) => String(opt.projectId) === targetProjId);
        const targetSelection = { projectId: targetProjId };
        if (matchedProjectRow?.projectName && matchedProjectRow.projectName !== targetProjId) {
          targetSelection.projectName = matchedProjectRow.projectName;
        }
        freshConfig = forceColumnFilterSelection(freshConfig, targetSelection);
      }

      setTableConfig(freshConfig);

      // 4. Enrich rows with action buttons
      const enrichedRows = rows.map((row) => ({
        ...row,
        action: (
          <div className="csat-action-cell" onClick={(ev) => ev.stopPropagation()}>
            {canUpdate && (
              <button type="button" className="csat-action-btn edit-btn" title="Edit" onClick={() => openEdit(row)}>
                <img src={EditActionIcon} alt="Edit" />
              </button>
            )}
            {canDelete && (
              <button type="button" className="csat-action-btn delete-btn" title="Delete" onClick={() => openDelete(row)}>
                <img src={DeleteActionIcon} alt="Delete" />
              </button>
            )}
          </div>
        ),
      }));

      // 5. Update pagination state
      const { page, limit, total } = response.meta || {};
      setPagination((prev) => {
        if (prev.page === page && prev.limit === limit && prev.total === total) return prev;
        return { page: page ?? 1, limit: limit ?? 10, total: total ?? 0 };
      });
      if (page) currentPageRef.current = Number(page) || 1;

      // 6. Write enriched rows back into response.data so TableUtility renders them
      response.data = {
        ...response.data,
        tableConfiguration: freshConfig,
        tableData: enrichedRows,
        data: {
          ...(response.data?.data || {}),
          tableConfiguration: freshConfig,
          tableData: enrichedRows,
        },
      };

      return normalizeTableUtilityResponse(response);
    },
    // projectFilterOptionsRef (ref) is used instead of projectFilterOptions state
    // so that changes to filter options do NOT recreate this callback — which would
    // otherwise rebuild `configuration` and re-initialise TableUtility on every fetch.
    [canUpdate, canDelete, openEdit, openDelete, projectIdParam, selectedProjectIdOverride, projectOptions],
  );

  /* ── Delete confirm ─────────────────────────────────────────────────── */
  const confirmDelete = useCallback(async () => {
    const customerId = dialog.row?.customerId ?? dialog.row?.customer_id;
    if (!customerId || isActionInProgress) return;
    setIsActionInProgress(true);
    try {
      const response = await UseAxios(deleteCsatCustomerDetails(customerId));
      if (response && response.status === 200) {
        toast.success('Customer deleted successfully.');
        closeDialog();
        refreshTable();
      } else {
        throw new Error('Failed to delete customer.');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to delete customer.');
    } finally {
      setIsActionInProgress(false);
    }
  }, [dialog.row, isActionInProgress, closeDialog, refreshTable]);

  // Selects the given project in the projectId/projectName filter after Add/Save.
  // Remounting the table (same as the existing URL ?projectId= flow) is required:
  // the dynamic-table package merges each API response's filter state with the
  // table's own live configuration, so pushing a selection into a table that
  // already has an (unselected) configuration gets silently overwritten.
  // A fresh mount has no prior configuration to merge against, so the forced
  // selection from handleFilteredApiResponse survives.
  const applyProjectFilter = useCallback(
    (projId) => {
      const targetProjId = String(projId ?? '').trim();
      if (!targetProjId) return;

      isInitialFilterApplied.current = false;
      initialProjectIdRef.current = targetProjId;
      setSelectedProjectIdOverride(targetProjId);
      refreshTable();
    },
    [refreshTable],
  );

  const handleSuccess = useCallback(
    (message, projectId) => {
      toast.success(message);
      if (projectId) {
        applyProjectFilter(projectId);
      } else {
        refreshTable();
      }
    },
    [refreshTable, applyProjectFilter],
  );

  /* ── Configuration  (exact ProjectEmployeeCost pattern) ─────────────── */
  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        userInfo: {
          id: userProfile?.id,
          emailId: userProfile?.email,
          permissions: userProfile?.permissions,
          roleDetails: userProfile?.roleDetails,
          employeeId: userProfile?.employeeId,
          globalColors: userProfile?.siteConfiguration?.global_colors,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: 'filter', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          // Same as ProjectEmployeeCost — the export request returns the structured
          // data directly in the backend response format.
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('CSAT Customer Details'),
            sheetName: () => getExportFileNameWithDate('CSAT Customer Details'),
          },
        },
        handler: {
          handleFilteredApiRequest,
          handleFilteredApiResponse,
          setTableStateHandler: (stateValue) => {
            const resolved = typeof stateValue === 'function' ? stateValue() : stateValue;
            if (resolved && typeof resolved === 'object') {
              tableStateRef.current = resolved;
            }
            if (resolved?.pagination) {
              const nextPage = Number(resolved.pagination.page) || 1;
              const nextLimit = Number(resolved.pagination.limit) || 10;
              currentPageRef.current = nextPage;
              setPagination((prev) => {
                if (prev.page === nextPage && prev.limit === nextLimit) return prev;
                return { ...prev, page: nextPage, limit: nextLimit };
              });
            }
            if (isInitialFilterApplied.current) {
              const params = new URLSearchParams(window.location.search);
              if (params.has('projectId')) {
                params.delete('projectId');
                setSearchParams(params, { replace: true });
              }
            }
          },
        },
        handlers: {
          handleAddIconClick: openAdd,
        },
        headers: { token: userProfile?.access_token || '' },
        showPagination: true,
        showToolBar: true,
        toolBarSettings: {
          showAddNew: canCreate,
          showDownloadExcel: true,
          showDownloadCsv: false,
          showColumnCustomize: false,
          showColumnFilterMenu: true,
          showHelp: false,
          showSearch: false,
          showToolBarHeading: true,
          toolBarHeading: 'CSAT Customer Details',
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          showFilterOption: true,
          additionalElements: [<CsatMoreActionsMenu key="csat-cd-more-actions" />],
        },
      },
      showPagination: true,
      tableSettings: {
        size: 'small',
        enableTreeTable: false,
        tableConfig,
        pageSize: 10,
        loaderSkeleton: { rows: 10, bodyCells: 2, columns: tableConfig.length },
        paginationConfig: {
          // Use currentPageRef.current (not pagination state) so config rebuilds
          // never snap the table back to page 1 – identical to CsatHistory pattern.
          page: currentPageRef.current,
          limit: pagination.limit,
          total: pagination.total,
          onPaginationChange: handlePaginationChange,
          rowsPerPageOptions: [10, 15, 25, 50],
          showRowsPerPage: true,
        },
        showExpandCollapse: false,
        tableScrollHeight: '66vh',
        maxFrozenColumns: { desktop: 0, tablet: 0, mobile: 0 },
      },
    }),
    // Stable dep rules (mirrors CsatHistory):
    //   • handleFilteredApiResponse is NOT listed — it would change whenever
    //     projectFilterOptions change (every data fetch), triggering a full
    //     TableUtility re-init and snapping pagination back to page 1.
    //   • tableConfig is NOT listed — it is rebuilt on every API response via
    //     setTableConfig(); excluding it prevents config rebuilds per fetch.
    //   • canUpdate / canDelete ARE listed so that when permissions load the
    //     new handleFilteredApiResponse (with correct action buttons) is captured.
    //   • pagination.total and pagination.limit are listed so the paginator shows the correct page count and page size.
    [userProfile, canCreate, canUpdate, canDelete, openAdd, handlePaginationChange, setSearchParams, pagination.total, pagination.limit],
  );

  const tableKey = `csat-customer-details-table-${tableRefreshNonce}`;

  if (!canView) return <Navigate to="/home" replace />;

  return (
    <div className="csat-cd-page-wrapper">
      <div className="csat-cd-table-container">
        <TableUtility key={tableKey} configuration={configuration} showFilterOption />
      </div>

      <CustomerFormDialog
        visible={dialog.type === 'add' || dialog.type === 'edit'}
        mode={dialog.type === 'edit' ? 'edit' : 'add'}
        rowData={dialog.row}
        onHide={closeDialog}
        onSuccess={handleSuccess}
        token={userProfile?.access_token}
        projectOptions={accessibleProjectOptions}
        defaultProjectId={selectedProjectIdOverride || projectIdParam || initialProjectIdRef.current || ''}
      />

      <DeleteModal
        isOpen={dialog.type === 'delete'}
        onClose={closeDialog}
        onConfirm={confirmDelete}
        isSubmitting={isActionInProgress}
      />
    </div>
  );
};

export default CsatCustomerDetails;
